import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Shield, Smartphone, KeyRound, AlertTriangle, CheckCircle2, Copy } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const buildAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const GovMFA = () => {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [disableForm, setDisableForm] = useState({ password: '', code: '' });
  const [loading, setLoading] = useState(false);

  const refreshStatus = useCallback(async () => {
    const s = await axios.get(`${API}/auth/mfa/status`, buildAuthHeader());
    setStatus(s.data);
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const beginSetup = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/mfa/setup`, {}, buildAuthHeader());
      setSetupData(r.data);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const enable = async () => {
    if (code.length < 6) return toast({ title: 'Enter the 6-digit code', variant: 'destructive' });
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/enable`, { code }, buildAuthHeader());
      toast({ title: 'Two-factor enabled', description: 'You will be prompted for a code on next sign-in.' });
      setSetupData(null);
      setCode('');
      await refreshStatus();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const disable = async () => {
    if (!disableForm.password || disableForm.code.length < 6) return toast({ title: 'Password and code required', variant: 'destructive' });
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, disableForm, buildAuthHeader());
      toast({ title: 'Two-factor disabled' });
      setDisableForm({ password: '', code: '' });
      await refreshStatus();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const copySecret = () => {
    if (!setupData) return;
    navigator.clipboard.writeText(setupData.secret);
    toast({ title: 'Secret copied' });
  };

  const qrSrc = setupData ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(setupData.provisioning_uri)}` : null;

  if (!status) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="gov-mfa-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2"><Shield className="h-6 w-6 text-blue-700" /> Two-Factor Authentication</h1>
        <p className="text-sm text-slate-500 mt-1">Protect your DES portal account with a time-based one-time password (TOTP) using Google Authenticator, Authy, 1Password, or any compatible app.</p>
      </div>

      {status.mfa_enabled ? (
        <Card className="border-2 border-emerald-300 bg-emerald-50/50">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="font-semibold">Two-factor authentication is enabled.</span></div>
            <p className="text-xs text-slate-600">You'll be asked for a 6-digit code from your authenticator app on each sign-in.</p>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center gap-2 text-red-700"><AlertTriangle className="h-4 w-4" /><span className="text-sm font-medium">Disable two-factor</span></div>
              <p className="text-xs text-slate-500">Confirm with your password and a current code.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div><Label>Password</Label><Input type="password" value={disableForm.password} onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })} data-testid="mfa-disable-password" /></div>
                <div><Label>6-digit code</Label><Input inputMode="numeric" maxLength={6} value={disableForm.code} onChange={(e) => setDisableForm({ ...disableForm, code: e.target.value })} data-testid="mfa-disable-code" /></div>
              </div>
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={disable} disabled={loading} data-testid="mfa-disable-btn">Disable two-factor</Button>
            </div>
          </CardContent>
        </Card>
      ) : !setupData ? (
        <Card className="border-2">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-slate-700"><Smartphone className="h-5 w-5" /><span className="font-semibold">Two-factor authentication is OFF.</span></div>
            <p className="text-xs text-slate-500">Recommended for all government portal users.</p>
            <Button onClick={beginSetup} disabled={loading} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="mfa-setup-start">
              <KeyRound className="h-4 w-4 mr-1" /> Set up two-factor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-blue-300">
          <CardContent className="p-5 space-y-4">
            <div className="text-sm font-semibold">Step 1 — Scan the QR code with your authenticator app</div>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              {qrSrc && <img src={qrSrc} alt="MFA QR" className="w-44 h-44 border rounded" data-testid="mfa-qr" />}
              <div className="text-xs text-slate-600 space-y-1 flex-1">
                <div>If you can't scan, enter the secret manually:</div>
                <div className="font-mono bg-slate-100 rounded p-2 break-all flex items-center justify-between gap-2">
                  <span data-testid="mfa-secret">{setupData.secret}</span>
                  <button onClick={copySecret} className="text-blue-700"><Copy className="h-3.5 w-3.5" /></button>
                </div>
                <div className="text-slate-400">Account: {setupData.account}<br />Issuer: {setupData.issuer}</div>
              </div>
            </div>

            <div className="text-sm font-semibold pt-2">Step 2 — Enter the 6-digit code shown in your app</div>
            <Input inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-verify-code" />
            <div className="flex gap-2">
              <Button onClick={enable} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mfa-enable-confirm">Enable two-factor</Button>
              <Button variant="outline" onClick={() => { setSetupData(null); setCode(''); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GovMFA;
